import { defineStore } from 'pinia'
import { ref } from 'vue'
import apiInstance from '@/utils/api'
import { useAuthStore } from '@/stores/auth'

export interface PaymentMethodCard {
  brand: string
  last4: string
  exp_month: number
  exp_year: number
}

export interface PaymentMethodSepaDebit {
  last4: string
  bank_code: string | null
}

export interface PaymentMethod {
  id: string
  type: string
  is_default: boolean
  card: PaymentMethodCard | null
  sepa_debit: PaymentMethodSepaDebit | null
}

export interface SubscriptionItem {
  id: string
  price_id: string
  amount: number
  currency: string
  interval: string
  quantity: number
}

export interface Subscription {
  id: string
  status: string
  billing_period: string | null
  start_date: string | null
  next_renewal_date: string | null
  current_period_start: number
  current_period_end: number
  cancel_at_period_end: boolean
  active_licenses_count: number | 0
  items: SubscriptionItem[]
}

export interface PrepaidLicense {
  id: string
  license_key: string | null
  restaurant_name: string | null
  license_type: string
  purchased_at: string | null
  current_period_end: string | null
  status: string
}

export interface UsedPrepaidKey {
  key: string
  license_id: string | null
  restaurant_name: string | null
  license_type: string | null
}

export type LicenseType = 'self_service' | 'service' | 'kiosk_terminal'
export type BillingPeriod = 'monthly' | 'annual'
export type PriceInterval = 'month' | 'year' | string

export interface Price {
  id: string
  currency: string
  unit_amount: number
  recurring: {
    interval: PriceInterval
  }
  product?: {
    name?: string
  }
}

export interface LicenseEntry {
  license_type: LicenseType
  kiosk_id?: number
}

export interface CreateCheckoutSessionPayload {
  restaurant_id: number
  billing_period: BillingPeriod
  price_id: string
  licenses: LicenseEntry[]
  promotion_code?: string
}

export type CheckoutSessionType = 'checkout' | 'quantity_update' | 'prepaid' | 'invoice'

/** Admin-issued prepayment invoice, for customers with no card and no SEPA mandate. */
export interface CreateInvoicePrepaymentPayload {
  restaurant_id: number
  billing_period: BillingPeriod
  price_id: string
  licenses: LicenseEntry[]
  days_until_due?: number | null
}

export interface InvoicePrepaymentResult {
  type: 'invoice'
  id: string
  url: string | null
  invoice_number: string | null
  invoice_pdf: string | null
  amount_due: number | null
  currency: string | null
  due_date: string | null
}

export interface PrepaidLicenseResult {
  id: string
  license_type: LicenseType | string
  license_key: string
  expiry_date: string
  status: string
  kiosk_id?: number | null
}

export interface CheckoutSessionResult {
  type: CheckoutSessionType
  id: string
  url: string | null
  licenses?: PrepaidLicenseResult[]
}

export interface BillingDetails {
  company_name: string
  street: string
  city: string
  zip_code: string | number
  country_id: number | null
  vat_number: string | null
  skip_for_now: boolean
}

export interface UpdateBillingDetailsPayload {
  street: string
  city: string
  zip_code: string | number
  country_id: number
  vat_number?: string | null
}

export interface LicenseOwner {
  id: number
  name: string
  type: string
}

export interface License {
  id: string
  license_type: LicenseType | string
  billing_period: BillingPeriod | string
  status: string
  purchased_at: string | null
  current_period_end: string | null
  cancel_at_period_end: boolean
  cancelled_at: string | null
  restaurant: { id: number; name: string } | null
  kiosk: { id: number; name: string } | null
  /** Only sent by the admin licenses endpoint, where an outlet can hold licenses under two companies. */
  owner?: LicenseOwner | null
}

export type ReportLicenseStatus = 'active' | 'suspended' | 'cancelled' | 'pending' | 'failed'
export type ReportLicenseType = 'self_service' | 'service' | 'kiosk_terminal'
export type ReportBillingPeriod = 'monthly' | 'annual'

export interface ReportLicense {
  id: string
  license_type: ReportLicenseType
  billing_period: ReportBillingPeriod
  status: ReportLicenseStatus
  purchased_at: string
  current_period_end: string
  cancel_at_period_end: boolean
  cancelled_at: string | null
  terminal_id: string | null
  license_key_source: string | null
}

export interface ReportOutlet {
  outlet_id: number
  outlet_name: string
  licenses: ReportLicense[]
}

export interface ReportCustomer {
  end_customer_id: number
  end_customer_name: string
  partner_id: number | null
  partner_name: string | null
  total_licenses: number
  outlets: ReportOutlet[]
}

export interface ReportSummary {
  total: number
  active: number
  suspended: number
  cancelled: number
}

export interface ReportData {
  summary: ReportSummary
  licenses: ReportCustomer[]
}

export interface ReportFilters {
  start_date: string
  end_date: string
  restaurant_id?: number[]
  license_type?: ReportLicenseType[]
  status?: ReportLicenseStatus[]
  billing_period?: ReportBillingPeriod[]
}

export interface Terminal {
  id: number
  email: string
}

export interface TerminalLicense {
  terminal_id?: number
  kiosk?: { id: number; name?: string } | null
  status: 'active' | 'suspended' | 'cancelled' | string
}

export const useLicenseStore = defineStore('license', () => {
  const billingDetails = ref<BillingDetails | null>(null)
  const billingDetailsLoading = ref(false)
  const billingDetailsError = ref<string | null>(null)
  const billingDetailsUpdateLoading = ref(false)
  const billingDetailsUpdateError = ref<string | null>(null)

  const paymentMethods = ref<PaymentMethod[]>([])
  const paymentMethodsLoading = ref(false)
  const paymentMethodsError = ref<string | null>(null)

  const subscriptions = ref<Subscription[]>([])
  const prepaidLicenses = ref<PrepaidLicense[]>([])
  const usedPrepaidKeys = ref<UsedPrepaidKey[]>([])
  const availablePrepaidKeys = ref<string[]>([])
  const subscriptionsLoading = ref(false)
  const subscriptionsError = ref<string | null>(null)

  const fetchPaymentMethods = async () => {
    paymentMethodsLoading.value = true
    paymentMethodsError.value = null
    try {
      const response = await apiInstance.get('/billing/payment-methods')
      const data = response.data
      if (data?.error === false && Array.isArray(data.data)) {
        paymentMethods.value = data.data as PaymentMethod[]
      } else {
        paymentMethodsError.value = data?.message || 'Failed to fetch payment methods'
      }
    } catch (err: any) {
      paymentMethodsError.value = err.response?.data?.message || 'Network error'
    } finally {
      paymentMethodsLoading.value = false
    }
  }

  const fetchBillingDetails = async (): Promise<BillingDetails | null> => {
    billingDetailsLoading.value = true
    billingDetailsError.value = null
    try {
      const response = await apiInstance.get('/billing/billing-details')
      const data = response.data
      if (data?.error === false && data.data) {
        billingDetails.value = data.data as BillingDetails
        return billingDetails.value
      } else {
        billingDetailsError.value = data?.message || 'Failed to fetch billing details'
        return null
      }
    } catch (err: any) {
      billingDetailsError.value = err.response?.data?.message || 'Network error'
      return null
    } finally {
      billingDetailsLoading.value = false
    }
  }

  const updateBillingDetails = async (payload: UpdateBillingDetailsPayload): Promise<boolean> => {
    billingDetailsUpdateLoading.value = true
    billingDetailsUpdateError.value = null
    try {
      const response = await apiInstance.put('/billing/billing-details', payload)
      const data = response.data
      if (data?.error === false) {
        billingDetails.value = { ...billingDetails.value!, ...payload }
        // Billing details and the profile update modal edit the same company record through
        // different stores. Mirror the change onto the auth copy so the modal doesn't keep
        // showing the values loaded at login until the next full page refresh.
        // Resolved here rather than in setup: the auth module imports back into this one.
        const authStore = useAuthStore()
        if (authStore.user) {
          authStore.user = {
            ...authStore.user,
            street: payload.street,
            city: payload.city,
            zipCode: String(payload.zip_code),
            countryId: payload.country_id,
            ...(payload.vat_number !== undefined ? { vatNumber: payload.vat_number } : {}),
          }
        }
        return true
      } else {
        billingDetailsUpdateError.value = data?.message || 'Failed to update billing details'
        return false
      }
    } catch (err: any) {
      billingDetailsUpdateError.value = err.response?.data?.message || 'Network error'
      return false
    } finally {
      billingDetailsUpdateLoading.value = false
    }
  }

  const fetchSubscriptions = async () => {
    subscriptionsLoading.value = true
    subscriptionsError.value = null
    try {
      const response = await apiInstance.get('/billing/subscriptions')
      const data = response.data
      if (data?.error === false && Array.isArray(data.data)) {
        subscriptions.value = data.data as Subscription[]
        prepaidLicenses.value = Array.isArray(data.prepaid_licenses) ? (data.prepaid_licenses as PrepaidLicense[]) : []
        usedPrepaidKeys.value = Array.isArray(data.used_prepaid_keys) ? (data.used_prepaid_keys as UsedPrepaidKey[]) : []
        availablePrepaidKeys.value = Array.isArray(data.available_prepaid_keys) ? (data.available_prepaid_keys as string[]) : []
      } else {
        subscriptionsError.value = data?.message || 'Failed to fetch subscriptions'
      }
    } catch (err: any) {
      subscriptionsError.value = err.response?.data?.message || 'Network error'
    } finally {
      subscriptionsLoading.value = false
    }
  }

  const prices = ref<Price[]>([])
  const pricesLoading = ref(false)
  const pricesError = ref<string | null>(null)

  const fetchPrices = async () => {
    if (pricesLoading.value) return
    pricesLoading.value = true
    pricesError.value = null
    try {
      const response = await apiInstance.get('/billing/prices')
      const data = response.data
      if (data?.error === false && Array.isArray(data.data)) {
        prices.value = data.data as Price[]
      } else {
        pricesError.value = data?.message || 'Failed to fetch prices'
      }
    } catch (err: any) {
      pricesError.value = err.response?.data?.message || 'Network error'
    } finally {
      pricesLoading.value = false
    }
  }

  const restaurantLicenses = ref<License[]>([])
  const restaurantLicensesLoading = ref(false)
  const restaurantLicensesError = ref<string | null>(null)

  const fetchRestaurantLicenses = async (restaurantId: number) => {
    restaurantLicensesLoading.value = true
    restaurantLicensesError.value = null
    restaurantLicenses.value = []
    try {
      const response = await apiInstance.get('/billing/get-licenses', { params: { restaurant_id: restaurantId } })
      const data = response.data
      if (data?.error === false && Array.isArray(data.data)) {
        restaurantLicenses.value = data.data as License[]
      } else {
        restaurantLicensesError.value = data?.message || 'Failed to fetch restaurant licenses'
      }
    } catch (err: any) {
      restaurantLicensesError.value = err.response?.data?.message || 'Network error'
    } finally {
      restaurantLicensesLoading.value = false
    }
  }

  const licenses = ref<Record<string, License[]>>({})
  const licensesLoading = ref(false)
  const licensesError = ref<string | null>(null)

  const fetchLicenses = async (subscriptionId: string) => {
    licensesLoading.value = true
    licensesError.value = null
    try {
      const response = await apiInstance.get(`/billing/${subscriptionId}/licenses`)
      const data = response.data
      if (data?.error === false && Array.isArray(data.data)) {
        licenses.value = { ...licenses.value, [subscriptionId]: data.data as License[] }
      } else {
        licensesError.value = data?.message || 'Failed to fetch licenses'
      }
    } catch (err: any) {
      licensesError.value = err.response?.data?.message || 'Network error'
    } finally {
      licensesLoading.value = false
    }
  }

  const cancelLicenseLoading = ref<string | null>(null)
  const cancelLicenseError = ref<string | null>(null)

  const cancelLicense = async (licenseId: string): Promise<boolean> => {
    cancelLicenseLoading.value = licenseId
    cancelLicenseError.value = null
    try {
      const response = await apiInstance.put(`/billing/licenses/${licenseId}`)
      const data = response.data
      if (data?.error === false) {
        return true
      }
      cancelLicenseError.value = data?.message || 'Failed to cancel license'
      return false
    } catch (err: any) {
      cancelLicenseError.value = err.response?.data?.message || 'Network error'
      return false
    } finally {
      cancelLicenseLoading.value = null
    }
  }

  const checkoutSessionLoading = ref(false)
  const checkoutSessionError = ref<string | null>(null)
  /** Per-field errors from a 422, e.g. promotion_code. */
  const checkoutSessionFieldErrors = ref<Record<string, string[]>>({})

  const createCheckoutSession = async (
    payload: CreateCheckoutSessionPayload,
  ): Promise<CheckoutSessionResult | null> => {
    if (checkoutSessionLoading.value) return null
    checkoutSessionLoading.value = true
    checkoutSessionError.value = null
    checkoutSessionFieldErrors.value = {}
    try {
      const response = await apiInstance.post('/billing/checkout-session', payload)
      const data = response.data
      const payloadData = data?.data
      if (data?.error === false && payloadData?.type && payloadData?.id) {
        return {
          type: payloadData.type as CheckoutSessionType,
          id: String(payloadData.id),
          url: payloadData.url ?? null,
          licenses: Array.isArray(payloadData.licenses) ? (payloadData.licenses as PrepaidLicenseResult[]) : undefined,
        }
      }
      checkoutSessionError.value = data?.message || 'Failed to create checkout session'
      return null
    } catch (err: any) {
      checkoutSessionFieldErrors.value = err.response?.data?.errors ?? {}
      // A rejected promotion code is shown under its field, so it is not raised as a general error too.
      if (!checkoutSessionFieldErrors.value.promotion_code) {
        checkoutSessionError.value = err.response?.data?.message || 'Network error'
      }
      return null
    } finally {
      checkoutSessionLoading.value = false
    }
  }

  // Admin-issued invoice prepayment. Kept separate from the checkout-session state so the
  // customer-facing purchase flow is unaffected.
  const invoicePrepaymentLoading = ref(false)
  const invoicePrepaymentError = ref<string | null>(null)
  const invoicePrepaymentPrices = ref<Price[]>([])
  const invoicePrepaymentPricesLoading = ref(false)
  const invoicePrepaymentPricesError = ref<string | null>(null)
  /** The company the invoice will be raised against: the outlet's managing partner, or its own company. */
  const invoicePrepaymentBilledTo = ref<LicenseOwner | null>(null)

  /**
   * Prices for the company that owns `restaurantId`. Admins have no company of their own, and
   * reseller vs standard pricing depends on the target company, so this is resolved per restaurant.
   */
  const fetchInvoicePrepaymentPrices = async (restaurantId: number) => {
    invoicePrepaymentPricesLoading.value = true
    invoicePrepaymentPricesError.value = null
    invoicePrepaymentPrices.value = []
    invoicePrepaymentBilledTo.value = null
    try {
      const response = await apiInstance.get('/billing/invoice-prepayment/prices', {
        params: { restaurant_id: restaurantId },
      })
      const data = response.data
      if (data?.error === false && Array.isArray(data.data)) {
        invoicePrepaymentPrices.value = data.data as Price[]
        invoicePrepaymentBilledTo.value = (data.billed_to as LicenseOwner) ?? null
      } else {
        invoicePrepaymentPricesError.value = data?.message || 'Failed to fetch prices'
      }
    } catch (err: any) {
      invoicePrepaymentPricesError.value = err.response?.data?.message || 'Network error'
    } finally {
      invoicePrepaymentPricesLoading.value = false
    }
  }

  const invoicePrepaymentLicenses = ref<License[]>([])
  const invoicePrepaymentLicensesLoading = ref(false)

  /** Existing licenses for a restaurant, resolved via the restaurant's own company. */
  const fetchInvoicePrepaymentLicenses = async (restaurantId: number) => {
    invoicePrepaymentLicensesLoading.value = true
    invoicePrepaymentLicenses.value = []
    try {
      const response = await apiInstance.get('/billing/invoice-prepayment/existing-licenses', {
        params: { restaurant_id: restaurantId },
      })
      const data = response.data
      if (data?.error === false && Array.isArray(data.data)) {
        invoicePrepaymentLicenses.value = data.data as License[]
      }
    } catch {
      // Non-blocking: this list is informational, so a failure must not stop the admin invoicing.
      invoicePrepaymentLicenses.value = []
    } finally {
      invoicePrepaymentLicensesLoading.value = false
    }
  }

  const createInvoicePrepayment = async (
    payload: CreateInvoicePrepaymentPayload,
  ): Promise<InvoicePrepaymentResult | null> => {
    if (invoicePrepaymentLoading.value) return null
    invoicePrepaymentLoading.value = true
    invoicePrepaymentError.value = null
    try {
      const response = await apiInstance.post('/billing/invoice-prepayment', payload)
      const data = response.data
      const payloadData = data?.data
      if (data?.error === false && payloadData?.id) {
        return {
          type: 'invoice',
          id: String(payloadData.id),
          url: payloadData.url ?? null,
          invoice_number: payloadData.invoice_number ?? null,
          invoice_pdf: payloadData.invoice_pdf ?? null,
          amount_due: payloadData.amount_due ?? null,
          currency: payloadData.currency ?? null,
          due_date: payloadData.due_date ?? null,
        }
      }
      invoicePrepaymentError.value = data?.message || 'Failed to create the prepayment invoice'
      return null
    } catch (err: any) {
      invoicePrepaymentError.value = err.response?.data?.message || 'Network error'
      return null
    } finally {
      invoicePrepaymentLoading.value = false
    }
  }

  const customerPortalLoading = ref(false)
  const customerPortalError = ref<string | null>(null)

  const openCustomerPortal = async () => {
    if (customerPortalLoading.value) return
    customerPortalLoading.value = true
    customerPortalError.value = null
    try {
      const response = await apiInstance.get('/billing/customer-portal')
      const url = response.data?.data?.url
      if (url) {
        window.open(url, '_blank', 'noopener, noreferrer')
      } else {
        customerPortalError.value = response.data?.message || 'Failed to open billing portal'
      }
    } catch (err: any) {
      customerPortalError.value = err.response?.data?.message || 'Failed to open billing portal'
    } finally {
      customerPortalLoading.value = false
    }
  }

  const createSetupIntent = async (): Promise<string | null> => {
    try {
      const response = await apiInstance.get('/billing/setup-intent')
      return response.data?.data?.client_secret ?? null
    } catch (err: any) {
      throw new Error(err.response?.data?.message || 'Failed to initialize card setup')
    }
  }

  const setDefaultPaymentMethod = async (paymentMethodId: string): Promise<boolean> => {
    try {
      const response = await apiInstance.get(`/billing/payment-methods/${paymentMethodId}/set-default`)
      if (response.data?.error === false) {
        await fetchPaymentMethods()
        return true
      }
      throw new Error(response.data?.message || 'Failed to set default payment method')
    } catch (err: any) {
      throw new Error(err.response?.data?.message || err.message || 'Failed to set default payment method')
    }
  }

  const deletePaymentMethod = async (paymentMethodId: string): Promise<boolean> => {
    try {
      const response = await apiInstance.delete(`/billing/payment-methods/${paymentMethodId}`)
      if (response.data?.error === false) {
        await fetchPaymentMethods()
        return true
      }
      throw new Error(response.data?.message || 'Failed to delete payment method')
    } catch (err: any) {
      throw new Error(err.response?.data?.message || err.message || 'Failed to delete payment method')
    }
  }

  const reportData = ref<ReportData | null>(null)
  const reportLoading = ref(false)
  const reportError = ref<string | null>(null)

  const fetchReport = async (filters: ReportFilters) => {
    reportLoading.value = true
    reportError.value = null
    try {
      const response = await apiInstance.post('/billing/report', filters)
      const data = response.data
      if (data?.error === false && data.data) {
        reportData.value = data.data as ReportData
      } else {
        reportError.value = data?.message || 'Failed to fetch report'
      }
    } catch (err: any) {
      reportError.value = err.response?.data?.message || 'Network error'
    } finally {
      reportLoading.value = false
    }
  }

  const terminals = ref<Terminal[]>([])
  const terminalLicenses = ref<TerminalLicense[]>([])
  const terminalsLoading = ref(false)
  const terminalsError = ref<string | null>(null)

  // silent: refresh in place (no loading flag, lists not cleared) so badges don't flicker
  const fetchTerminals = async (restaurantId: number, { silent = false }: { silent?: boolean } = {}) => {
    if (!silent) {
      terminalsLoading.value = true
      terminals.value = []
      terminalLicenses.value = []
    }
    terminalsError.value = null
    try {
      const response = await apiInstance.get(`/restaurant/${restaurantId}/self-terminal`)
      const data = response.data
      if (data?.error === false) {
        terminals.value = Array.isArray(data.data?.terminals) ? (data.data.terminals as Terminal[]) : []
        terminalLicenses.value = Array.isArray(data.data?.licenses) ? (data.data.licenses as TerminalLicense[]) : []
      } else {
        terminalsError.value = data?.message || 'Failed to fetch terminals'
      }
    } catch (err: any) {
      terminalsError.value = err.response?.data?.message || 'Network error'
    } finally {
      if (!silent) terminalsLoading.value = false
    }
  }

  const reset = () => {
    paymentMethods.value = []
    paymentMethodsError.value = null
    subscriptions.value = []
    prepaidLicenses.value = []
    usedPrepaidKeys.value = []
    availablePrepaidKeys.value = []
    subscriptionsError.value = null
    prices.value = []
    pricesError.value = null
    licenses.value = {}
    licensesError.value = null
    restaurantLicenses.value = []
    restaurantLicensesError.value = null
    cancelLicenseError.value = null
    checkoutSessionError.value = null
    checkoutSessionFieldErrors.value = {}
    invoicePrepaymentError.value = null
    invoicePrepaymentPrices.value = []
    invoicePrepaymentPricesError.value = null
    invoicePrepaymentBilledTo.value = null
    invoicePrepaymentLicenses.value = []
    reportData.value = null
    reportError.value = null
    terminals.value = []
    terminalLicenses.value = []
    terminalsError.value = null
  }

  return {
    billingDetails,
    billingDetailsLoading,
    billingDetailsError,
    billingDetailsUpdateLoading,
    billingDetailsUpdateError,
    fetchBillingDetails,
    updateBillingDetails,
    paymentMethods,
    paymentMethodsLoading,
    paymentMethodsError,
    subscriptions,
    prepaidLicenses,
    usedPrepaidKeys,
    availablePrepaidKeys,
    subscriptionsLoading,
    subscriptionsError,
    prices,
    pricesLoading,
    pricesError,
    licenses,
    licensesLoading,
    licensesError,
    cancelLicenseLoading,
    cancelLicenseError,
    checkoutSessionLoading,
    checkoutSessionError,
    checkoutSessionFieldErrors,
    restaurantLicenses,
    restaurantLicensesLoading,
    restaurantLicensesError,
    reportData,
    reportLoading,
    reportError,
    fetchReport,
    customerPortalLoading,
    customerPortalError,
    fetchRestaurantLicenses,
    terminals,
    terminalLicenses,
    terminalsLoading,
    terminalsError,
    fetchTerminals,
    openCustomerPortal,
    fetchPaymentMethods,
    fetchSubscriptions,
    fetchPrices,
    fetchLicenses,
    cancelLicense,
    createCheckoutSession,
    invoicePrepaymentLoading,
    invoicePrepaymentError,
    invoicePrepaymentPrices,
    invoicePrepaymentPricesLoading,
    invoicePrepaymentPricesError,
    fetchInvoicePrepaymentPrices,
    invoicePrepaymentBilledTo,
    invoicePrepaymentLicenses,
    invoicePrepaymentLicensesLoading,
    fetchInvoicePrepaymentLicenses,
    createInvoicePrepayment,
    createSetupIntent,
    setDefaultPaymentMethod,
    deletePaymentMethod,
    reset,
  }
})
